/**
 * Get current breakpoint based off of current screen size.
 *
 * @example
 * // screen size is 700px
 * // 'md' breakpoint is "max-width: 800px"
 * useBreakpoint(700) returns 'md'
 */
import scssBreakpoints from '../scss/breakpoints.module.scss';
import useWindowSize from './useWindowSize';

export const breakpointsKeys = ['xxs', 'xs', 's', 'm', 'l', 'xl'] as const;
export const breakpointsKeysWithDefault = [
  ...breakpointsKeys,
  'default',
] as const;

export type Breakpoint = typeof breakpointsKeys[number];
export type BreakpointWithDefault = Breakpoint | 'default';

export type Breakpoints = Record<Breakpoint, number>;
export type BreakpointsWithDefault = Record<BreakpointWithDefault, number>;

const breakpoints = transformScssBreakpoints(
  scssBreakpoints as unknown as Record<string, string>,
);

/**
 * Utils
 */
export function transformScssBreakpoints(breakpointsFromScss: {
  [breakpoint: string]: string;
}): Breakpoints {
  const breakpointPrefix = 'breakpoint-';
  const filteredBreakpoints = Object.keys(breakpointsFromScss).filter(
    (breakpointFromScss) => breakpointFromScss.includes(breakpointPrefix),
  );
  const breakpointKey = filteredBreakpoints.map((breakpointFromScss) =>
    breakpointFromScss.replace('breakpoint-', ''),
  );
  const breakpoints = breakpointKey.reduce(
    (breakpointsObj: Record<string, number>, breakpoint: string) => {
      const breakpointValue = breakpointsFromScss[
        breakpointPrefix + breakpoint
      ].replace('px', '');

      breakpointsObj[breakpoint] = +breakpointValue;

      return breakpointsObj;
    },
    {},
  ) as Breakpoints;

  return breakpoints;
}

function getCurrentBreakpointByWidth(
  width: number,
  breakpoints: Breakpoints,
): BreakpointWithDefault {
  let currentBreakpoint: BreakpointWithDefault = 'default';

  breakpointsKeys.forEach((breakpointKey) => {
    const breakpointIndex = breakpointsKeys.indexOf(breakpointKey);
    const previousBreakpoint = breakpointsKeys[breakpointIndex - 1];
    const breakpointMaxWidth = breakpoints[previousBreakpoint];
    const currentBreakpointKey = breakpointsKeys[breakpointIndex];
    const isLowerThanSmallestBreakpoint =
      width < breakpoints[breakpointsKeysWithDefault[0]];
    const isHigherThanLargestBreakpoint =
      width >= breakpoints[breakpointsKeys[breakpointsKeys.length - 1]];

    if (width >= breakpointMaxWidth) currentBreakpoint = currentBreakpointKey;
    if (isLowerThanSmallestBreakpoint)
      currentBreakpoint = breakpointsKeysWithDefault[0];
    if (isHigherThanLargestBreakpoint) currentBreakpoint = 'default';
  });

  return currentBreakpoint;
}

export function getBreakpointIndex(breakpointKey: BreakpointWithDefault) {
  return breakpointsKeysWithDefault.indexOf(breakpointKey);
}

/**
 * Hook
 */
export function useBreakpoint() {
  const [width] = useWindowSize();
  const currentBreakpoint = getCurrentBreakpointByWidth(width, breakpoints);

  return currentBreakpoint;
}
